import { z } from "zod";

/**
 * The identity profile returned by login, refresh, invitation acceptance, and GET /auth/me/
 * (auth-v1.md "Identity profile response"). This is the ambient "who is the user" contract —
 * it lives in core so any module reads it from `@core` without a cross-module import.
 */
export const ProfileUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  // Editable on « Mon compte »; older answers may lack them.
  first_name: z.string().default(""),
  last_name: z.string().default(""),
  // The API contract still allows `ar`; the app ships fr and en only, so any other value reads
  // as French rather than failing the whole profile parse.
  preferred_language: z.enum(["fr", "en"]).catch("fr"),
  // Display label only ("president", "administrator", or null for accounts outside the new
  // model). Never branch on it: access follows `capabilities` and per-resource
  // `available_actions`, so a new role needs no frontend change.
  role: z.string().nullable().catch(null),
});

export const ProfileSchema = z.object({
  user: ProfileUserSchema,
  // Account state, not a capability: applicants may draft before verifying, and only submission
  // requires a verified address.
  email_verified: z.boolean(),
  // e.g. "ACTIVE" | "PENDING_VERIFICATION"; kept open (string) — status set may grow and this
  // pass doesn't branch on it.
  account_status: z.string(),
  // Route-group and landing contract. Staff collapse to the single "team" surface; the staff tree
  // is one group. Do not read these as a capability list.
  available_surfaces: z.array(z.string()),
  default_surface: z.string(),
  /**
   * What this account may do in the app at all (auth-v1.md "capabilities"): `team.read`,
   * `team.invitation.manage` for the President, and the global `cohorts.*` facts used by the
   * sidebar. Per-cohort controls still use that cohort's `available_actions`. Read it through
   * `@core/permissions`, never directly.
   */
  capabilities: z.array(z.string()).default([]),
  session_id: z.string(),
  /** Safe post-auth redirect target; "" when none was supplied or stored (auth-v1.md). */
  next: z.string(),
});

export type ProfileUser = z.infer<typeof ProfileUserSchema>;
export type Profile = z.infer<typeof ProfileSchema>;
